import * as Sentry from '@sentry/react'
import { isCancel } from 'axios'
import { isRetryableError as isApiError } from 'axios-retry'
import { compact, includes, merge } from 'lodash/fp'
import LogRocket from 'logrocket'
import { applyMiddleware, compose, createStore } from 'redux'
import createAxiosMiddleware from 'redux-axios-middleware'
import { composeWithDevTools } from 'redux-devtools-extension'
import persistState, { mergePersistedState } from 'redux-localstorage'
import filter from 'redux-localstorage-filter'
import adapter from 'redux-localstorage/lib/adapters/localStorage'
import { createLogger } from 'redux-logger'
import createThrottleMiddleware from 'redux-throttle'
import thunkMiddleware from 'redux-thunk'

import actions from '@/actions'
import { ERROR_TYPE_API } from '@/core/constants/constants'
import {
  LEARNPATH_ELEMENTS_CHANGED_TOAST_EVENT,
  isLearnpathElementNotFoundError,
} from '@/core/utils/learnpathElementNotFoundError'
import { getLocalStorageItem } from '@/core/utils/localStorage/getLocalStorageItem'
import { env } from '@/env'
import createRootReducer from '@/reducers'

import axios from './axios'
import isLocalStorageEnabled from './utils/localStorage/isLocalStorageEnabled'
import actionSanitizer from './utils/logrocket/actionSanitizer'
import stateSanitizer from './utils/logrocket/stateSanitizer'
import validateReduxState from './utils/redux/validateReduxState'
import createCancelAssetUploadMiddleware from './utils/s3/createCancelAssetUploadMiddleware'
import updateMixpanelLastLogin from './utils/tracking/updateMixpanelLastLogin'

const {
  REACT_APP_PERSIST_ALL,
  DEV,
  REACT_APP_LOGROCKET_KEY,
  REACT_APP_SENTRY_DSN,
  REACT_APP_REDUX_LOGGER_ENABLED,
} = env

const persistAll = REACT_APP_PERSIST_ALL || !!getLocalStorageItem('persistAll')

export default function configureStore({ initialState = {} }) {
  const axiosMiddleware = createAxiosMiddleware(axios, {
    returnRejectedPromiseOnError: true,
    onComplete: updateMixpanelLastLogin,
    interceptors: {
      response: [
        {
          error: ({ dispatch }, error) => {
            if (isLearnpathElementNotFoundError(error)) {
              window.dispatchEvent(
                new CustomEvent(LEARNPATH_ELEMENTS_CHANGED_TOAST_EVENT),
              )
              return Promise.reject(error)
            }
            if (isApiError(error) && !isCancel(error)) {
              dispatch(actions.core.error(ERROR_TYPE_API))
            }
            return Promise.reject(error)
          },
        },
      ],
    },
  })

  const throttleMiddleware = createThrottleMiddleware(500, {
    leading: false,
    trailing: true,
  })

  const middlewares = [
    throttleMiddleware,
    axiosMiddleware,
    thunkMiddleware,
    createCancelAssetUploadMiddleware(),
  ]

  // Use this predicate for dev tools and logger to avoid spam.
  const predicate = (getState, action) =>
    !includes(action.type, [
      'CORE/REGIONS/ADD_REGION',
      'CORE/REGIONS/REMOVE_REGION',
    ])

  if (REACT_APP_REDUX_LOGGER_ENABLED) {
    middlewares.push(createLogger({ predicate, collapsed: true }))
  }

  if (REACT_APP_LOGROCKET_KEY) {
    middlewares.push(
      LogRocket.reduxMiddleware({ stateSanitizer, actionSanitizer }),
    )
  }

  const storage =
    isLocalStorageEnabled() &&
    compose(filter(persistAll ? null : ['form']))(adapter(window.localStorage))

  const enhancers = compact([
    applyMiddleware(...middlewares),
    storage ? persistState(storage, 'masterplan') : null,
    REACT_APP_SENTRY_DSN
      ? Sentry.createReduxEnhancer({
          actionTransformer: actionSanitizer,
          stateTransformer: stateSanitizer,
        })
      : null,
    validateReduxState,
  ])

  const enhancer = composeWithDevTools({ predicate })(...enhancers)

  const reducer = compose(mergePersistedState(merge), createRootReducer)()

  // @ts-expect-error TS(2345): Argument of type 'unknown' is not assignable to pa... Remove this comment to see the full error message
  const store = createStore(reducer, initialState, enhancer)

  // TODO: Failing test, it will be fixed in [MP-10691]
  // if (import.meta.hot) {
  //   import.meta.hot.accept('../reducers', () => {
  //     const nextRootReducer = require('../reducers')
  //     store.replaceReducer(nextRootReducer)
  //   })
  // }

  if (DEV) {
    // @ts-expect-error TS(2339): Property 'store' does not exist on type 'Window & ... Remove this comment to see the full error message
    window.store = store
    // @ts-expect-error TS(2339): Property 'axios' does not exist on type 'Window & ... Remove this comment to see the full error message
    window.axios = axios
  }

  return store
}
